import {
  normalizeConsumerEntitlements,
  type AccessKind,
  type ConsumerEntitlements,
  type ConsumerTier,
  type ResolvedConsumerAccess,
} from "@pulse/shared";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth";
import { SubscriptionRequiredError } from "@/lib/entitlementErrors";
import { supabase } from "@/lib/supabase";

export type ConsumerSubscriptionSource =
  | "stripe"
  | "apple"
  | "google"
  | "platform"
  | "internal"
  | "founder"
  | "insider"
  | "earlybird"
  | "freemium"
  | string;

const EXPIRED_ACCESS: ResolvedConsumerAccess & { subscriptionSource: null } = {
  accessKind: "expired",
  tier: null,
  entitlements: null,
  subscriptionSource: null,
};

export type ConsumerEntitlementsState = ResolvedConsumerAccess & {
  subscriptionSource: ConsumerSubscriptionSource | null;
  isLoading: boolean;
  isRefetching: boolean;
  isError: boolean;
  hasLoaded: boolean;
  error: unknown;
  refetch: () => Promise<unknown>;
};

export const consumerEntitlementsQueryKey = (userId: string | undefined) =>
  ["consumer-entitlements", userId ?? "none"] as const;

let subscriptionReadyCache: { userId: string; promise: Promise<void> } | null = null;

export function resetConsumerSubscriptionReadyCache(): void {
  subscriptionReadyCache = null;
}

export async function ensureConsumerPlatformTrial(): Promise<void> {
  const { error } = await supabase.rpc("ensure_consumer_platform_trial");
  if (error) throw error;
}

export type RpcConsumerEntitlements = ResolvedConsumerAccess & {
  subscriptionSource: ConsumerSubscriptionSource | null;
};

export async function fetchConsumerEntitlementsRpc(): Promise<RpcConsumerEntitlements> {
  const { data, error } = await supabase.rpc("get_consumer_entitlements");
  if (error) throw error;

  const raw = (data ?? {}) as {
    accessKind?: AccessKind;
    tier?: string | null;
    subscriptionSource?: string | null;
    entitlements?: ConsumerEntitlements | null;
  };

  const tier =
    raw.tier === "free" ||
    raw.tier === "member" ||
    raw.tier === "circle_plus" ||
    raw.tier === "elite"
      ? raw.tier
      : null;

  return {
    accessKind: raw.accessKind ?? "expired",
    tier,
    entitlements: normalizeConsumerEntitlements(raw.entitlements, tier),
    subscriptionSource: raw.subscriptionSource ?? null,
  };
}

/** True when legacy ensure should try Free → Elite trial upgrade (same DB row). */
function shouldAttemptLegacyPlatformTrial(resolved: RpcConsumerEntitlements): boolean {
  if (resolved.accessKind === "expired") return true;
  // Freemium row with no trial history yet — do not key off "missing subscription".
  return (
    resolved.tier === "free" &&
    resolved.accessKind === "active" &&
    (resolved.subscriptionSource === "freemium" || resolved.subscriptionSource == null)
  );
}

export async function loadConsumerEntitlements(): Promise<RpcConsumerEntitlements> {
  let resolved = await fetchConsumerEntitlementsRpc();

  // New users: trial starts only in welcome finish().
  // Legacy / backfilled users with welcome_completed: upgrade freemium → Elite trial
  // in place when still eligible (trial_start history), even if accessKind is active free.
  if (!shouldAttemptLegacyPlatformTrial(resolved)) return resolved;

  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user?.id) return resolved;

  const { data: onboarding } = await supabase
    .from("consumer_onboarding_state")
    .select("welcome_completed")
    .eq("user_id", user.id)
    .maybeSingle();

  if (!onboarding?.welcome_completed) return resolved;

  try {
    await ensureConsumerPlatformTrial();
    resolved = await fetchConsumerEntitlementsRpc();
  } catch {
    /* one-shot already consumed or RPC rejected — keep freemium/expired */
  }
  return resolved;
}

export async function ensureConsumerSubscriptionReady(): Promise<void> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return;

  if (subscriptionReadyCache?.userId === user.id) {
    return subscriptionReadyCache.promise;
  }

  const promise = (async () => {
    const resolved = await loadConsumerEntitlements();
    if (resolved.accessKind === "expired") {
      throw new SubscriptionRequiredError();
    }
  })();

  subscriptionReadyCache = { userId: user.id, promise };
  try {
    await promise;
  } catch (error) {
    if (subscriptionReadyCache?.promise === promise) {
      subscriptionReadyCache = null;
    }
    throw error;
  }
}

export function isFounderSubscriptionSource(
  source: ConsumerSubscriptionSource | null | undefined,
): boolean {
  return source === "founder" || source === "internal";
}

export function isComplimentaryEliteSource(
  source: ConsumerSubscriptionSource | null | undefined,
): boolean {
  return source === "founder" || source === "internal" || source === "insider";
}

export function useConsumerEntitlements(): ConsumerEntitlementsState {
  const { user, isGuest } = useAuth();
  const userId = user?.id && !isGuest ? user.id : undefined;

  const query = useQuery({
    queryKey: consumerEntitlementsQueryKey(userId),
    queryFn: () => loadConsumerEntitlements(),
    enabled: !!userId,
    staleTime: 60_000,
    retry: 3,
    retryDelay: (attempt) => Math.min(1_000 * 2 ** attempt, 10_000),
  });

  const resolved = query.isSuccess && query.data ? query.data : EXPIRED_ACCESS;

  return {
    ...resolved,
    hasLoaded: !!userId && query.isSuccess,
    isLoading: !!userId && query.isPending,
    isRefetching: !!userId && query.isFetching && query.isSuccess,
    isError: query.isError,
    error: query.error,
    refetch: async () => {
      resetConsumerSubscriptionReadyCache();
      return query.refetch();
    },
  };
}

/** Single source of truth for subscription gating (interested toggle, etc.). */
export function useConsumerSubscriptionReady() {
  const { user, isGuest } = useAuth();
  const userId = user?.id && !isGuest ? user.id : undefined;
  const entitlements = useConsumerEntitlements();

  const hasAccess =
    entitlements.hasLoaded && entitlements.accessKind !== "expired";

  return {
    isReady: !userId || hasAccess,
    isProvisioning: !!userId && !entitlements.hasLoaded && entitlements.isLoading,
    isError: entitlements.isError,
    error: entitlements.isError ? entitlements.error : null,
    retry: entitlements.refetch,
    hasLoaded: entitlements.hasLoaded,
    accessKind: entitlements.accessKind,
  };
}

export function formatConsumerSubscriptionLabel(
  tier: ConsumerTier | null,
  accessKind: AccessKind,
  subscriptionSource: ConsumerSubscriptionSource | null,
  labels: {
    tierLabels: Record<ConsumerTier, string>;
    noSubscription: string;
    founderAccess: string;
    internalAccess: string;
    insiderAccess: string;
    earlybirdAccess: string;
    trialAccess: string;
  },
): string {
  if (!tier || accessKind === "expired" || !labels.tierLabels[tier]) {
    return labels.noSubscription;
  }

  const base = labels.tierLabels[tier];
  if (subscriptionSource === "founder") {
    return `${base} · ${labels.founderAccess}`;
  }
  if (subscriptionSource === "internal") {
    return `${base} · ${labels.internalAccess}`;
  }
  if (subscriptionSource === "insider") {
    return `${base} · ${labels.insiderAccess}`;
  }
  if (subscriptionSource === "earlybird") {
    return `${base} · ${labels.earlybirdAccess}`;
  }
  if (accessKind === "trialing" || subscriptionSource === "platform") {
    return `${base} · ${labels.trialAccess}`;
  }
  return base;
}
